import { IPROP_MAP, modifyProps } from '../../generators/modifyProps';

import { BadgeProps } from '../jdsbadge/logic';
import { ButtonProps } from '../jdsbutton/logic';
import { JDSServiceCardProps } from './generated';
import { LinkProps } from '../jdslink/logic';
import { MouseEvent } from 'react';
import { resolvePropObjectKeys } from '../../utils/componenthelpers';

const legacySizes = ['xxs', 's', 'xs'] as const;
type legacySizesType = (typeof legacySizes)[number];

export interface ServiceCardProps
  extends Omit<
    JDSServiceCardProps,
    'size' | 'description' | 'primaryCTA' | 'image'
  > {
  /**
   * Determines the size of the ContentBlock. Size dictates the text and spacing.
   * For size=`xxs`, the layout also changes if only a single CTA is sent.
   */
  size?: legacySizesType | JDSServiceCardProps['size'];
  /**
   * Accepts description as string. JSX.Element is also allowed in case you have
   * anchor tags inside your richtext.
   * Passing a JSX elements is deprecated. Please pass a string always
   */
  description?: string | JSX.Element;
  /**
   * Please see `Link>LinkType`. The `button={}` parameter can be sent to overwrite
   * default button behaviours. E.g. different kind, destructive, with icons etc.
   */
  primaryCTA?:
    | string
    | {
        /**
         * @deprecated
         * Pass the string to primaryCTA directly
         */
        title?: string;
        /**
         * @deprecated
         * use `onPrimaryClick`
         */
        onClick?: (ev: MouseEvent<HTMLDivElement>) => void;
        /**
         * @deprecated
         */
        _button?: ButtonProps;
      };
  /**
   * Image HTML tags. e.g. `<img>`
   *
   * Passing an image element is deprecated. Pass the image source as as string directly
   */
  image: string | JSX.Element;
  /**
   * Badge Component as a Caption
   * @deprecated
   * use `badgeIcon` and `badgeLabel`
   */
  caption?: BadgeProps;
  /**
   * Service Card can also accept custom children for further customisation.
   */
  children?: React.ReactNode;
  /**
   * When present, the entire card is clickable
   * Eg. fullCardCTA:{onClick: ()={}}
   */
  fullCardCTA?: LinkProps;
}

export const PROP_MAP: IPROP_MAP<ServiceCardProps> = (props) => {
  if (typeof props.description === 'object') {
    console.warn('passing description as an element is deprecated');
  }
  if (typeof props.image === 'object') {
    console.warn('passing image as an element is deprecated');
  }
  return {
    image: {
      properties: {
        image: resolvePropObjectKeys(props.image, 'props.src', '')
      }
    },
    caption: {
      properties: {
        badgeLabel: props.caption?.label,
        badgeIcon: props.caption?.icon
      }
    },
    size: {
      defaultValue: 'xxs',
      variations: {
        s: {
          size: 'small'
        },
        xs: {
          size: 'xtra-small'
        },
        xxs: {
          size: 'xtra-xtra-small'
        }
      }
    }
  };
};

export const withLogic =
  (
    Component: React.ComponentType<ServiceCardProps>
  ): React.FC<ServiceCardProps> =>
  (userProps: ServiceCardProps) => {
    const props = modifyProps(userProps, PROP_MAP);
    return <Component {...props} />;
  };
